package diana.soleil.design.iraniancanadienbazzar.feature.events

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalUriHandler
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.common.ShareService
import diana.soleil.design.iraniancanadienbazzar.core.data.EventRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ShareConfig
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BusinessCover
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.StatusBadge
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.cityLabel
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_detail_about
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_detail_to
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_detail_when
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_detail_where
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_details_external
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_error
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_featured
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_free
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_get_free_tickets
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_going
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_going_count
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_im_going
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_image_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_link_unavailable
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_retry
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_share_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_tickets_organizer
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_venue_format
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/** Rich event page (Stitch `97c7693e`): cover, when/where, description, RSVP + tickets. */
data class EventDetailUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val event: Event? = null,
    val going: Boolean = false,
    val goingCount: Int = 0,
)

class EventDetailViewModel(
    private val repository: EventRepository,
    private val analytics: AnalyticsTracker,
    private val eventId: String,
) : ViewModel() {

    private val eventFlow = MutableStateFlow<Event?>(null)
    private val loadFailed = MutableStateFlow(false)
    private val goingCount = MutableStateFlow(0)
    val rsvpSignInRequired = MutableStateFlow(false)

    val state: StateFlow<EventDetailUiState> =
        combine(eventFlow, loadFailed, repository.observeMyRsvps(), goingCount) { event, failed, myRsvps, count ->
            EventDetailUiState(
                loading = event == null && !failed,
                error = failed,
                event = event,
                going = event != null && event.id in myRsvps,
                goingCount = count,
            )
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), EventDetailUiState())

    init {
        load()
    }

    fun load() {
        viewModelScope.launch {
            loadFailed.value = false
            when (val result = repository.getEvent(eventId)) {
                is DataResult.Success -> {
                    eventFlow.value = result.data
                    analytics.track(
                        AnalyticsEvents.EVENT_VIEW,
                        mapOf(
                            AnalyticsParams.EVENT_ID to result.data.id,
                            AnalyticsParams.METRO_ID to (result.data.metroId ?: "all"),
                        ),
                    )
                    goingCount.value = repository.rsvpCounts(listOf(eventId))[eventId] ?: 0
                }
                is DataResult.Failure -> loadFailed.value = true
            }
        }
    }

    fun toggleGoing(going: Boolean) {
        viewModelScope.launch {
            when (val result = repository.setRsvp(eventId, going)) {
                is DataResult.Success ->
                    goingCount.value = (goingCount.value + if (going) 1 else -1).coerceAtLeast(0)
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) rsvpSignInRequired.value = true
            }
        }
    }

    fun trackOutlink() {
        eventFlow.value?.let { event ->
            analytics.track(
                AnalyticsEvents.EVENT_OUTLINK,
                mapOf(
                    AnalyticsParams.EVENT_ID to event.id,
                    AnalyticsParams.METRO_ID to (event.metroId ?: "all"),
                ),
            )
        }
    }
}

@Composable
fun EventDetailRoute(
    eventId: String,
    onBack: () -> Unit,
    onGetFreeTickets: (eventId: String) -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: EventDetailViewModel = koinViewModel(parameters = { parametersOf(eventId) }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val rsvpGate by viewModel.rsvpSignInRequired.collectAsStateWithLifecycle()
    androidx.compose.runtime.LaunchedEffect(rsvpGate) {
        if (rsvpGate) {
            viewModel.rsvpSignInRequired.value = false
            onRequireSignIn()
        }
    }
    val uriHandler = LocalUriHandler.current
    val shareService = koinInject<ShareService>()
    val shareConfig = koinInject<ShareConfig>()
    EventDetailScreen(
        state = state,
        onBack = onBack,
        onRetry = viewModel::load,
        onToggleGoing = viewModel::toggleGoing,
        onTickets = { event ->
            val url = event.ticketUrl?.takeIf { it.isNotBlank() }
            when {
                url == null && event.isFree -> onGetFreeTickets(event.id)
                url != null -> {
                    viewModel.trackOutlink()
                    uriHandler.openUri(url.normalizedWebUrl())
                }
            }
        },
        onShare = { event, title ->
            shareService.share(listOf(title, shareConfig.eventLink(event.id)).joinToString("\n"))
        },
        modifier = modifier,
    )
}

@Composable
fun EventDetailScreen(
    state: EventDetailUiState,
    onBack: () -> Unit,
    onRetry: () -> Unit,
    onToggleGoing: (going: Boolean) -> Unit,
    onTickets: (Event) -> Unit,
    onShare: (Event, String) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxHeight().background(AppTheme.colors.background).appContentWidth(AppTheme.sizes.contentMaxWidthCompact)) {
        Row(
            modifier = Modifier.fillMaxWidth().statusBarsPadding().padding(AppTheme.spacing.lg),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.events_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onSurface,
            )
        }
        when {
            state.loading -> LoadingState(modifier = Modifier.fillMaxSize())
            state.error || state.event == null -> ErrorState(
                message = stringResource(Res.string.events_error),
                onRetry = onRetry,
                retryLabel = stringResource(Res.string.events_retry),
            )
            else -> {
                val event = state.event
                val title = event.title.resolve(AppTheme.language)
                val lang = AppTheme.language
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .navigationBarsPadding()
                        .padding(horizontal = AppTheme.spacing.lg)
                        .padding(bottom = AppTheme.spacing.xl),
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
                ) {
                    androidx.compose.foundation.layout.Box(
                        modifier = Modifier.fillMaxWidth().aspectRatio(16f / 9f),
                        contentAlignment = Alignment.TopStart,
                    ) {
                        BusinessCover(
                            imageUrl = event.coverUrl,
                            contentDescription = stringResource(Res.string.events_image_a11y, title),
                            iconName = "event",
                            modifier = Modifier.fillMaxWidth().aspectRatio(16f / 9f).clip(AppTheme.shapes.card),
                        )
                        val badge = when {
                            event.isFree -> stringResource(Res.string.events_free)
                            event.featured -> stringResource(Res.string.events_featured)
                            else -> null
                        }
                        if (badge != null) {
                            StatusBadge(label = badge, modifier = Modifier.padding(AppTheme.spacing.sm))
                        }
                    }
                    Text(text = title, style = AppTheme.typography.headlineLg, color = AppTheme.colors.onSurface)

                    // When
                    DetailBlock("schedule", stringResource(Res.string.events_detail_when)) {
                        val start = eventDateTime(event.startsAtMillis)
                        val label = event.endsAtMillis?.let { end -> stringResource(Res.string.events_detail_to, start, eventDateTime(end)) } ?: start
                        Text(text = label, style = AppTheme.typography.bodyMd, color = AppTheme.colors.onSurfaceVariant)
                    }
                    // Where
                    DetailBlock("location_on", stringResource(Res.string.events_detail_where)) {
                        Text(
                            text = stringResource(Res.string.events_venue_format, event.venue.name, AppTheme.language.cityLabel(event.venue.city)),
                            style = AppTheme.typography.bodyMd,
                            color = AppTheme.colors.onSurfaceVariant,
                        )
                        if (event.venue.address.isNotBlank()) {
                            Text(text = event.venue.address, style = AppTheme.typography.bodySm, color = AppTheme.colors.onSurfaceVariant)
                        }
                    }

                    // RSVP
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                        AppButton(
                            text = stringResource(if (state.going) Res.string.events_going else Res.string.events_im_going),
                            onClick = { onToggleGoing(!state.going) },
                            variant = if (state.going) AppButtonVariant.Secondary else AppButtonVariant.Text,
                            leadingIcon = if (state.going) "check" else "event_available",
                        )
                        if (state.goingCount > 0) {
                            Text(
                                text = stringResource(Res.string.events_going_count, state.goingCount),
                                style = AppTheme.typography.labelSm,
                                color = AppTheme.colors.onSurfaceVariant,
                            )
                        }
                    }

                    // About
                    val description = event.description.resolve(lang)
                    if (description.isNotBlank()) {
                        DetailBlock("info", stringResource(Res.string.events_detail_about)) {
                            Text(text = description, style = AppTheme.typography.bodyMd, color = AppTheme.colors.onSurfaceVariant)
                        }
                    }

                    // Ticket / external action
                    val outlink = !event.ticketUrl.isNullOrBlank()
                    val inApp = !outlink && event.isFree
                    val actionLabel = when {
                        inApp -> stringResource(Res.string.events_get_free_tickets)
                        outlink -> stringResource(Res.string.events_tickets_organizer)
                        else -> stringResource(Res.string.events_link_unavailable)
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm), verticalAlignment = Alignment.CenterVertically) {
                        AppButton(
                            text = actionLabel,
                            onClick = { onTickets(event) },
                            enabled = outlink || inApp,
                            trailingIcon = if (outlink) "open_in_new" else null,
                            modifier = Modifier.weight(1f),
                        )
                        AppIconButton(
                            iconName = "share",
                            contentDescription = stringResource(Res.string.events_share_a11y),
                            onClick = { onShare(event, title) },
                        )
                    }
                }
            }
        }
    }
}

/** Icon + label header over a small block of detail text. */
@Composable
private fun DetailBlock(iconName: String, label: String, content: @Composable () -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs)) {
            AppIcon(name = iconName, size = AppTheme.sizes.iconSm, tint = AppTheme.colors.primary)
            Text(text = label, style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurface)
        }
        content()
    }
}
